<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { login_jueces } from "@/utils/api_backend";

const email = ref("");
const password = ref("");
const router = useRouter();
const isSubmitting = ref(false);

onMounted(() => {
  // Limpiamos rastro de tokens viejos al entrar al login por seguridad
  localStorage.removeItem("evento_token");
  localStorage.removeItem("refresh");
});

async function validarCredenciales() {
  if (!email.value || !password.value) {
    Swal.fire({
      icon: "warning",
      title: "Campos requeridos",
      text: "Por favor ingresa tu correo y contraseña.",
      confirmButtonColor: "#1a1a1a",
    });
    return;
  }

  isSubmitting.value = true;
  try {
    const data = await login_jueces({
      email: email.value,
      password: password.value,
    });

    // Validación extra
    if (!data || !data.access) {
      throw new Error("No se recibió el token de acceso.");
    }

    const tatamiId = data.juez?.tatami_asignado;
    if (!tatamiId || tatamiId === "null") {
      Swal.fire({
        icon: "warning",
        title: "Sin tatami asignado",
        text: "Tu usuario no tiene un tatami asignado. Contacta al administrador.",
        confirmButtonColor: "#1a1a1a",
      });
      return; // No navegar, no guardar tokens
    }

    // Guardar tokens en localStorage
    localStorage.setItem("evento_token", data.access);
    localStorage.setItem("refresh", data.refresh);

    // Redirigir al editor
    router.push(`/evento/tatami/${data.juez.tatami_asignado}`);
  } catch (error) {
    console.error("Error en login:", error);

    const mensajeError =
      error.response?.data?.non_field_errors?.[0] ||
      error.response?.data?.detail ||
      "Correo o contraseña incorrectos.";

    Swal.fire({
      icon: "error",
      title: "Acceso denegado",
      text: mensajeError,
      confirmButtonColor: "#d33",
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="login-wrapper">
    <div class="glass-login shadow-2xl" data-aos="zoom-in-down">
      <div class="text-center mb-4">
        <h4 class="text-white fw-black mb-1">Torneo EGP</h4>
        <p class="text-white-50 small tracking-widest text-uppercase">
          Acceso al sistema
        </p>
      </div>

      <form @submit.prevent="validarCredenciales">
        <div class="mb-4">
          <label class="form-label text-white-50 small fw-bold text-uppercase">
            Correo Electrónico
          </label>
          <div class="input-group-modern">
            <i class="bi bi-envelope-fill"></i>
            <input
              v-model="email"
              type="email"
              placeholder="coach@evolve.com"
              autocomplete="email"
              required
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="form-label text-white-50 small fw-bold text-uppercase">
            Contraseña
          </label>
          <div class="input-group-modern">
            <i class="bi bi-lock-fill"></i>
            <input
              v-model="password"
              type="password"
              placeholder="••••••••"
              autocomplete="current-password"
              required
            />
          </div>
        </div>

        <button class="btn-login mt-2" type="submit" :disabled="isSubmitting">
          <span v-if="!isSubmitting">INGRESAR</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;900&display=swap");

.login-wrapper {
  min-height: 100vh;
  font-family: "Inter", sans-serif;
  /* Mantenemos tu imagen original pero le agregamos el overlay oscuro para que el texto resalte */
  background: linear-gradient(rgba(20, 30, 40, 0.7), rgba(20, 30, 40, 0.8)),
    url("@/assets/img/Evolve/img_1.jpg");
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.glass-login {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 3rem 2rem;
}

.brand-logo-container {
  width: 120px;
  height: 80px;
  background-color: #ffffff;
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.brand-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.fw-black {
  font-weight: 900;
}

.tracking-widest {
  letter-spacing: 0.15em;
}

/* INPUTS MODERNOS */
.input-group-modern {
  position: relative;
  display: flex;
  align-items: center;
}

.input-group-modern i {
  position: absolute;
  left: 15px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
}

.input-group-modern input {
  width: 100%;
  background: rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px;
  padding: 12px 12px 12px 45px;
  color: white;
  transition: all 0.3s;
}

.input-group-modern input:focus {
  outline: none;
  background: rgba(0, 0, 0, 0.3) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
}

.input-group-modern input::placeholder {
  color: rgba(255, 255, 255, 0.3);
  font-style: italic;
}

/* BOTÓN PERSONALIZADO */
.btn-login {
  width: 100%;
  background: white;
  color: #1a1e23;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  transition: all 0.3s;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #f0f0f0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
